import React, { useState, useEffect } from 'react';

const ConfigSizeSelector = ({
  configOptions,
  selectedConfigs,
  onConfigChange,
  termin,
  disabled = false
}) => {
  const [configs, setConfigs] = useState({
    additional_cores: 2,
    additional_ram: 2,
    additional_space: 20
  });

  useEffect(() => {
    if (selectedConfigs) {
      setConfigs(selectedConfigs);
    }
  }, [selectedConfigs]);

  // Reset configs saat configOptions berubah (produk berubah) - hanya jika belum ada selectedConfigs
  useEffect(() => {
    if (configOptions && configOptions.length > 0 && !selectedConfigs) {
      const defaultConfigs = {
        additional_cores: 2,
        additional_ram: 2,
        additional_space: 20
      };
      setConfigs(defaultConfigs);
      // Reset parent state juga
      onConfigChange(defaultConfigs);
    }
  }, [configOptions, onConfigChange, selectedConfigs]);

  // Auto-update RAM ketika Core berubah
  useEffect(() => {
    if (!configOptions || configOptions.length === 0) return;

    const coreValue = configs.additional_cores || 2;
    const minRam = Math.max(2, Math.ceil(coreValue / 2)); // Minimal 2 GB, setengah dari Core
    const adjustedMinRam = Math.ceil(minRam / 2) * 2; // Pastikan kelipatan 2 GB
    
    // Jika RAM saat ini kurang dari minimum yang diperlukan, update otomatis
    if (configs.additional_ram < adjustedMinRam) {
      const newConfigs = { ...configs, additional_ram: adjustedMinRam };
      setConfigs(newConfigs);
      onConfigChange(newConfigs);
    }
  }, [configs, configOptions, onConfigChange]);

  // Validasi nilai configs agar selalu dalam range yang benar - hanya saat configOptions berubah
  useEffect(() => {
    if (!configOptions || configOptions.length === 0) return;

    const validatedConfigs = { ...configs };
    let hasChanges = false;

    // Validasi additional_cores (2-32 dengan step 2, base 2 Core)
    if (validatedConfigs.additional_cores < 2 || validatedConfigs.additional_cores > 32) {
      validatedConfigs.additional_cores = 2;
      hasChanges = true;
    } else if (validatedConfigs.additional_cores % 2 !== 0) {
      // Pastikan nilai genap
      validatedConfigs.additional_cores = Math.round(validatedConfigs.additional_cores / 2) * 2;
      hasChanges = true;
    }

    // Validasi additional_ram (2-64 dengan step 2, base 2 GB)
    if (validatedConfigs.additional_ram < 2 || validatedConfigs.additional_ram > 64) {
      validatedConfigs.additional_ram = 2;
      hasChanges = true;
    } else if (validatedConfigs.additional_ram % 2 !== 0) {
      // Pastikan nilai genap
      validatedConfigs.additional_ram = Math.round(validatedConfigs.additional_ram / 2) * 2;
      hasChanges = true;
    }

    // Validasi additional_space (20-500 dengan step 10, base 20 GB)
    if (validatedConfigs.additional_space < 20 || validatedConfigs.additional_space > 500) {
      validatedConfigs.additional_space = 20;
      hasChanges = true;
    } else if (validatedConfigs.additional_space % 10 !== 0) {
      // Pastikan nilai kelipatan 10
      validatedConfigs.additional_space = Math.round(validatedConfigs.additional_space / 10) * 10;
      hasChanges = true;
    }

    // Validasi RAM minimal setengah dari Core (base 2 Core, 2 GB)
    const coreValue = validatedConfigs.additional_cores || 2;
    const minRam = Math.max(2, Math.ceil(coreValue / 2)); // Minimal 2 GB, setengah dari Core
    // Pastikan RAM adalah kelipatan 2 GB
    const adjustedMinRam = Math.ceil(minRam / 2) * 2;
    if (validatedConfigs.additional_ram < adjustedMinRam) {
      validatedConfigs.additional_ram = adjustedMinRam;
      hasChanges = true;
    }

    // Validasi RAM maksimal berdasarkan Core
    const maxRam = coreValue === 2 ? 16 : 64;
    if (validatedConfigs.additional_ram > maxRam) {
      validatedConfigs.additional_ram = maxRam;
      hasChanges = true;
    }

    // Update state jika ada perubahan
    if (hasChanges) {
      setConfigs(validatedConfigs);
      onConfigChange(validatedConfigs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configOptions]); // Hanya jalankan saat configOptions berubah

  const handleConfigChange = (configType, value) => {
    const newConfigs = { ...configs, [configType]: value };
    setConfigs(newConfigs);
    onConfigChange(newConfigs);
  };

  const getConfigOption = (name) => {
    return configOptions?.find(option => option.name === name);
  };

  const getConfigPrice = (configType, value) => {
    const option = getConfigOption(configType);
    if (!option || !option.options || !option.options.option) return 0;

    // Base specs adalah 2 Core, 2GB RAM, 20GB Storage - semua gratis
    if (configType === 'additional_cores' && value <= 2) return 0;
    if (configType === 'additional_ram' && value <= 2) return 0;
    if (configType === 'additional_space' && value <= 20) return 0;

    // Hitung additional value (total - base) untuk konsistensi dengan Cart
    let additionalValue;
    if (configType === 'additional_cores') {
      additionalValue = value - 2; // total cores - base cores
    } else if (configType === 'additional_ram') {
      additionalValue = value - 2; // total GB - base GB
    } else if (configType === 'additional_space') {
      additionalValue = value - 20; // total GB - base GB
    } else {
      return 0;
    }

    // Cari opsi yang sesuai dengan nilai ADDITIONAL yang dipilih
    // Database menyimpan additional specs di optionname
    const selectedOption = option.options.option.find(opt => {
      if (configType === 'additional_cores') {
        // additionalValue = additional cores, database menyimpan additional cores
        return parseInt(opt.name) === additionalValue;
      } else if (configType === 'additional_ram') {
        // additionalValue = additional GB, database menyimpan additional MB
        const additionalRamInMB = additionalValue * 1024;
        return parseInt(opt.name) === additionalRamInMB;
      } else if (configType === 'additional_space') {
        // additionalValue = additional GB, database menyimpan additional GB
        return parseInt(opt.name) === additionalValue;
      }
      return false;
    });

    if (!selectedOption) return 0;

    const optionPrice = selectedOption.pricing?.IDR?.[termin];
    if (!optionPrice || optionPrice === "-1.00") return 0;

    return parseFloat(optionPrice);
  };

  const renderConfigSlider = (configType, label, unit, min, max, step = 1) => {
    const option = getConfigOption(configType);
    if (!option || !option.options || !option.options.option) return null;

    const currentValue = configs[configType];
    const currentPrice = getConfigPrice(configType, currentValue);


    // Validasi RAM berdasarkan Core (minimal setengah dari Core, base 2 Core, 2 GB)
    const getMaxRam = () => {
      if (configType === 'additional_ram') {
        const coreValue = configs.additional_cores || 2;
        if (coreValue === 2) return 16; // Max 16GB untuk 2 Core
        return 64; // Max 64GB untuk >2 Core
      }
      return max;
    };

    const getMinRam = () => {
      if (configType === 'additional_ram') {
        const coreValue = configs.additional_cores || 2;
        const minRam = Math.max(2, Math.ceil(coreValue / 2)); // Minimal 2 GB, setengah dari Core
        // Pastikan RAM adalah kelipatan 2 GB
        return Math.ceil(minRam / 2) * 2;
      }
      return min;
    };

    const effectiveMin = getMinRam();
    const effectiveMax = getMaxRam();

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="fw-bold mb-0">{label}</label>
          <div className="text-end">
            <div className="fw-bold">{currentValue} {unit}</div>
            {currentPrice > 0 && (
              <small className="text-muted">
                +{currentPrice.toLocaleString('id-ID')} / {termin}
              </small>
            )}
          </div>
        </div>

        <div className="position-relative">
          {/* Slider yang lebih besar tanpa input number */}
          <div className="mb-2">
            {/* Baris: Min, Slider, Max */}
            <div className="d-flex align-items-center">
              <span className="small text-muted" style={{ width: '30px', textAlign: 'center' }}>{effectiveMin}</span>
              <div className="flex-grow-1 mx-2">
                <input
                  type="range"
                  className="form-range"
                  min={effectiveMin}
                  max={effectiveMax}
                  step={step}
                  value={currentValue}
                  onChange={(e) => handleConfigChange(configType, parseInt(e.target.value))}
                  disabled={disabled}
                  style={{ 
                    height: '40px',
                    background: 'transparent'
                  }}
                />
              </div>
              <span className="small text-muted" style={{ width: '30px', textAlign: 'center' }}>{effectiveMax}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fallback: Jika validasi terlalu ketat, gunakan kondisi yang lebih sederhana
  const isBasicReady = configOptions && configOptions.length > 0;
  
  if (!isBasicReady) {
    return (
      <div className="config-size-selector">
        <h6 className="fw-bold mb-3">Konfigurasi Tambahan</h6>
        <div className="p-3 bg-light rounded d-flex align-items-center justify-content-center" style={{ minHeight: '200px' }}>
          <div className="text-center">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div>
              <small className="text-muted">
                Memuat konfigurasi tambahan...
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="config-size-selector">
      <h6 className="fw-bold mb-3">Konfigurasi Tambahan</h6>

      {renderConfigSlider('additional_cores', 'CPU Cores', 'Core', 2, 32, 2)}
      {renderConfigSlider('additional_ram', 'RAM', 'GB', 2, 64, 2)}
      {renderConfigSlider('additional_space', 'Storage', 'GB', 20, 500, 10)}

      {/* Info validasi */}
      {/* <div className="mt-3 p-3 bg-info bg-opacity-10 border border-info rounded">
          <small className="text-info">
            <strong>Base Specs VPS Custom:</strong><br/>
            • CPU: 2 Core (gratis), 4-32 Core (berbayar)<br/>
            • RAM: 2 GB (gratis), 4-64 GB (berbayar)<br/>
            • Storage: 20 GB (gratis), 30-500 GB (berbayar)<br/>
            <br/>
            <strong>Aturan Konfigurasi:</strong><br/>
            • CPU: Minimal setengah dari RAM, maksimal 16GB untuk 2 Core, maksimal 64GB untuk {'>'}2 Core<br/>
            • Pertambahan: CPU +2, RAM +2GB, Storage +10GB<br/>
            <br/>
            <strong>Rumus Harga:</strong><br/>
            • CPU: 2 Core = gratis, 4 Core = base × 1, 6 Core = base × 2<br/>
            • RAM: 2 GB (2048 MB) = gratis, 4 GB (4096 MB) = base × 1, 6 GB (6144 MB) = base × 2<br/>
            • Storage: 20 GB = gratis, 30 GB = base × 1, 40 GB = base × 2<br/>
            <br/>
            <strong>Catatan:</strong> RAM menggunakan MB sesuai Virtualizor WHMCS module
          </small>
        </div> */}

      <div className="mt-3 p-3 bg-light rounded border">
        {(() => {
          const totalPrice = Object.keys(configs).reduce((sum, configType) => {
            const price = getConfigPrice(configType, configs[configType]);
            return sum + price;
          }, 0);
          
          if (totalPrice > 0) {
            return (
              <div className="text-center">
                <div className="mb-1">
                  <span className="text-muted small">Total Tambahan:</span>
                </div>
                <div className="mb-1">
                  <span className="fw-bold text-success fs-5">
                    +{totalPrice.toLocaleString('id-ID')}
                  </span>
                </div>
                <div>
                  <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                    <i className="fas fa-info-circle me-1"></i>
                    Harga Belum Termasuk PPN
                  </span>
                </div>
              </div>
            );
          } else {
            return (
              <div className="text-center">
                <div className="mb-1">
                  <span className="text-muted small">Total Tambahan:</span>
                </div>
                <div>
                  <span className="fw-bold text-success fs-5">
                    Gratis
                  </span>
                </div>
              </div>
            );
          }
        })()}
      </div>
    </div>
  );
};

export default ConfigSizeSelector;
